import React, { useCallback, useImperativeHandle, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useDrag, useDrop } from 'react-dnd';

import { getBase64ImageInfo } from '@ecp/common/src/utils/fileUtil';
import boTheme from '@ecp/common/src/style/theme/bo';
import { FlexBoxColumn } from '@ecp/common/src/layouts/flex/styled';
import { Spacing } from '@ecp/common/src/layouts/Spacing';
import FileUploaderUnstyled, { FILE_SIZE } from '@ecp/common/src/components/unstyled/input/FileUploaderUnstyled';
import { ImageCardContainer, PreviewImageWrapper } from '@ecp/common/src/components/file/ImageCard';
import { DND_IMAGE_ICON } from '@ecp/common/src/const/imageFile';
import DeleteIcon from '@ecp/common/src/assets/icon/icon__bo__image-delete.svg';
import { ReactComponent as AddIcon } from '@ecp/common/src/assets/icon/icon__add.svg';

const ImageCard = React.forwardRef(
  (
    {
      desc,
      value: imageFile,
      onChange,
      onError,
      onDelete,
      draggable,
      onMove,
      keyValue,
      multiUpload,
      width,
      height,
      readOnly,
      extension,
      maxSize,
      text,
      ...props
    },
    ref
  ) => {
    const fileUploaderRef = useRef(null);
    useImperativeHandle(ref, () => fileUploaderRef.current.value);

    const handleChange = useCallback(
      async (e, fileList, result) => {
        if (result.errors.length) {
          e && (e.target.value = '');
          onError?.(result);
        } else {
          const file = fileList[0];
          const imgInfo = await getBase64ImageInfo(file);
          onChange?.({ ...imgInfo, file }, result);
        }
      },
      [onChange, onError]
    );

    const [{ isDragging }, drag] = useDrag(
      () => ({
        type: DND_IMAGE_ICON,
        item: { type: keyValue },
        collect: (monitor) => ({
          isDragging: !!monitor.isDragging(),
        }),
        end: async ({ type: dragType }, monitor) => {
          const didDrop = monitor.didDrop();
          if (!didDrop) {
            onMove(dragType, keyValue);
          }
        },
      }),
      [keyValue, onMove]
    );

    const [, drop] = useDrop(
      () => ({
        accept: DND_IMAGE_ICON,
        hover({ type: dragType }) {
          if (dragType !== keyValue) {
            onMove(dragType, keyValue);
          }
        },
      }),
      [onMove]
    );

    const handleDeleteClick = useCallback(() => {
      if (!multiUpload) {
        onChange?.({ file: '', url: '' });
      } else {
        onDelete?.(keyValue);
      }
    }, [keyValue, multiUpload, onChange, onDelete]);

    return (
      <ImageCardContainer ref={draggable ? (node) => drag(drop(node)) : undefined} opacity={isDragging ? 0 : 1}>
        {!multiUpload && desc && (
          <>
            <Description>{desc}</Description>
            <Spacing bottom={'6px'} />
          </>
        )}
        {imageFile?.url ? (
          <PreviewCard
            imageFile={imageFile}
            deleteButtonComponent={readOnly ? <></> : <DeleteButton onClick={handleDeleteClick} />}
            width={width}
            height={height}
            {...props}
          />
        ) : (
          <FileUploaderUnstyled
            buttonComponent={<EmptyCard width={width} height={height} text={text} />}
            onChange={handleChange}
            extension={extension}
            maxSize={maxSize}
            ref={fileUploaderRef}
          />
        )}
        {!multiUpload && (
          <>
            <Spacing top={'6px'} />
            <ImageName width={width}>{imageFile?.file?.name || imageFile?.originFileNameExt || ''}</ImageName>
          </>
        )}
      </ImageCardContainer>
    );
  }
);

ImageCard.displayName = 'ImageCard';

ImageCard.propTypes = {
  desc: PropTypes.string,
  value: PropTypes.object,
  onChange: PropTypes.func,
  onError: PropTypes.func,
  onDelete: PropTypes.func,
  draggable: PropTypes.bool,
  onMove: PropTypes.func,
  keyValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  multiUpload: PropTypes.bool,
  width: PropTypes.string,
  height: PropTypes.string,
  text:PropTypes.string,
  readOnly: PropTypes.bool,
  extension: PropTypes.arrayOf(PropTypes.string),
  maxSize: PropTypes.number,
};

ImageCard.defaultProps = {
  draggable: false,
  width: '90px',
  height: '90px',
  maxSize: FILE_SIZE.IMAGE,
};

export default ImageCard;

export const EmptyCard = ({ width, height, text }) => {
  return (
    <DefaultButtonComponent width={width} height={height}>
      <>
        <AddIcon />
        {text}
      </>
    </DefaultButtonComponent>
  );
};

EmptyCard.propTypes = {
  width: PropTypes.string,
  height: PropTypes.string,
  text: PropTypes.string,
};

EmptyCard.defaultProps = {
  width: '90px',
  height: '90px',
  text: '업로드',
};

const PreviewCard = ({ imageFile, deleteButtonComponent, width, height, onZoom, index }) => {
  return (
    <>
      <FlexBoxColumn>
        <PreviewImageWrapper width={width} height={height}>
          <img
            className="card-image"
            src={imageFile.url}
            alt={imageFile.url}
            onClick={() => onZoom(index, imageFile)}
          />
          {deleteButtonComponent}
        </PreviewImageWrapper>
      </FlexBoxColumn>
    </>
  );
};

PreviewCard.propTypes = {
  imageFile: PropTypes.object,
  deleteButtonComponent: PropTypes.oneOfType([PropTypes.elementType, PropTypes.element, PropTypes.string]),
  width: PropTypes.string,
  height: PropTypes.string,
  onZoom: PropTypes.func,
  index: PropTypes.number,
};

PreviewCard.defaultProps = {
  width: '90px',
  height: '90px',
};

const DefaultButtonComponent = styled(FlexBoxColumn)`
  justify-content: center;
  padding: 4px 0;
  pointer-events: none;
  gap: 2px;

  font-size: ${boTheme.font.size.small};
  font-weight: 400;
  color: #000000;

  width: ${({ width }) => width};
  height: ${({ height }) => height};
  background: #eeeeee;
  border: 1px solid #dfdfdf;
`;

const Description = styled.pre`
  text-align: center;
  font-size: ${boTheme.font.size.error};
  font-weight: ${boTheme.font.weight.regular};
  line-height: 130%;
  color: ${boTheme.color.sub2};
`;

const ImageName = styled.span`
  max-width: ${({ width }) => width};
  max-height: 30px;
  display: -webkit-box;
  word-wrap: break-word;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  overflow: hidden;
  text-align: center;

  font-size: ${boTheme.font.size.small};
  font-weight: ${boTheme.font.weight.regular};
  color: ${boTheme.color.sub1};
  line-height: 113%;
`;

const DeleteButton = styled.button`
  position: absolute;
  right: 6px;
  top: 6px;
  display: inline-block;
  width: 14px;
  height: 14px;
  background: url(${DeleteIcon}) no-repeat;
  font-size: 0;
  line-height: 0;
`;
